// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminCentros (migrado al shell · prompt 0471)
// CRUD de centros / sites (oficinas, plantas, delegaciones)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { fetchOrganizaciones, request } from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

interface Centro {
  id: string;
  codigo: string;
  nombre: string;
  tipoCentroId?: string;
  tipoCentro?: { nombre: string };
  direccion?: string;
  ciudad?: string;
  provincia?: string;
  codigoPostal?: string;
  paisId?: string;
  pais?: { id: string; nombre: string } | null;
  telefono?: string;
  personaContacto?: string;
  latitud?: number;
  longitud?: number;
  urlUbicacion?: string;
  estado?: string;
  organizacionId?: string;
}

function EditIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

// 0479.1a M2 — iconos canonicos power + trash (coherentes con patron RF/RG 0473/0479 M2)
function PowerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4 2.5C2.8 3.2 2 4.5 2 6c0 2.2 1.8 4 4 4s4-1.8 4-4c0-1.5-.8-2.8-2-3.5M6 1.5V6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2 3H10M4.5 3V2C4.5 1.5 4.8 1 5.5 1H6.5C7.2 1 7.5 1.5 7.5 2V3M3 3L3.5 10.5C3.5 10.8 3.8 11 4 11H8C8.2 11 8.5 10.8 8.5 10.5L9 3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ESTADO_STYLES: Record<string, { bg: string; text: string }> = {
  ACTIVO: { bg: '#DEF7EC', text: '#03543F' },
  INACTIVO: { bg: '#FDE8E8', text: '#9B1C1C' },
  EN_APERTURA: { bg: '#E1EFFE', text: '#1E40AF' },
  EN_CIERRE: { bg: '#FEF3C7', text: '#92400E' },
};

export function AdminCentros() {
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  const [centros, setCentros] = useState<Centro[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modal, setModal] = useState<Centro | 'new' | null>(null);
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    request<{ data: Centro[] }>('/apps/centros?porPagina=100')
      .then((r) => setCentros(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const q = search.toLowerCase().trim();
  const base = mostrarInactivos ? centros : centros.filter((c) => c.estado !== 'INACTIVO');
  const filtered = q
    ? base.filter(
        (c) =>
          c.codigo.toLowerCase().includes(q) ||
          c.nombre.toLowerCase().includes(q) ||
          (c.ciudad ?? '').toLowerCase().includes(q) ||
          (c.provincia ?? '').toLowerCase().includes(q) ||
          (c.tipoCentro?.nombre ?? '').toLowerCase().includes(q),
      )
    : base;

  const tooltips = (adm.tooltips as Record<string, string> | undefined) ?? {};

  // 0479.1a M2 — handlers toggle activo + borrar (patron canonico RF/RG/Puestos)
  const handleToggleActivo = async (c: Centro) => {
    const nuevoEstado = c.estado === 'INACTIVO' ? 'ACTIVO' : 'INACTIVO';
    try {
      await request(`/apps/centros/${c.id}`, {
        method: 'PUT',
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      load();
      showToast(nuevoEstado === 'ACTIVO' ? 'Centro activado' : 'Centro desactivado');
    } catch {
      showToast('Error al cambiar estado');
    }
  };

  const handleBorrar = async (c: Centro) => {
    if (!window.confirm(`Eliminar centro "${c.codigo} — ${c.nombre}"?`)) return;
    try {
      await request(`/apps/centros/${c.id}`, { method: 'DELETE' });
      load();
      showToast('Centro eliminado');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar';
      showToast(msg);
    }
  };

  const columns: AdminTableColumn<Centro>[] = [
    {
      key: 'codigo',
      label: adm.colCodigo ?? 'Código',
      mono: true,
      sortable: true,
      sortKey: (c) => c.codigo,
      render: (c) => <span>{c.codigo}</span>,
    },
    {
      key: 'nombre',
      label: adm.colNombre ?? adm.nombre ?? 'Nombre',
      sortable: true,
      sortKey: (c) => c.nombre,
      render: (c) => <span style={{ fontWeight: 600 }}>{c.nombre}</span>,
    },
    {
      key: 'tipo',
      label: adm.tipo ?? 'Tipo',
      sortable: true,
      sortKey: (c) => c.tipoCentro?.nombre ?? '',
      render: (c) => <span style={{ color: '#64748B' }}>{c.tipoCentro?.nombre ?? '—'}</span>,
    },
    {
      key: 'ubicacion',
      label: adm.ubicacion ?? 'Ubicación',
      render: (c) => (
        <span style={{ color: '#64748B' }}>
          {[c.ciudad, c.provincia].filter(Boolean).join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'pais',
      label: adm.pais ?? 'País',
      sortable: true,
      sortKey: (c) => c.pais?.nombre ?? '',
      render: (c) => <span style={{ color: '#64748B' }}>{c.pais?.nombre ?? '—'}</span>,
    },
    {
      key: 'estado',
      label: adm.estado ?? 'Estado',
      sortable: true,
      sortKey: (c) => c.estado ?? '',
      render: (c) => {
        const st = c.estado ?? 'ACTIVO';
        const palette = ESTADO_STYLES[st] ?? { bg: '#F3F4F6', text: '#374151' };
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              background: palette.bg,
              color: palette.text,
              whiteSpace: 'nowrap',
            }}
          >
            {st}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={adm.sites ?? adm.centros ?? 'Centros'}
        infoTooltip={{
          title: adm.sites ?? 'Centros',
          text: 'Ubicaciones físicas en las que opera el tenant: oficinas, plantas, delegaciones, almacenes, tiendas.\n\nCada centro pertenece a una organización y tiene un código único, dirección, coordenadas opcionales y estado (activo, inactivo, en apertura o en cierre).\n\nLos centros se usan para asignar personas, activos y solicitudes de compra a una ubicación concreta.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search ?? 'Buscar...',
        }}
        extras={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            {adm.mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
        onNew={{
          label: adm.newSite ?? 'Nuevo centro',
          onClick: () => setModal('new'),
        }}
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(c) => c.id}
        onRowClick={(c) => setModal(c)}
        loading={loading}
        loadingMessage={((t.common as Record<string, unknown>)?.cargando as string) ?? 'Cargando…'}
        emptyMessage={adm.noResults ?? 'Sin centros'}
        actions={(c) => (
          <>
            <AdminActionButton
              icon={<EditIcon />}
              label={tooltips.editar ?? 'Editar'}
              onClick={() => setModal(c)}
            />
            <AdminActionButton
              icon={<PowerIcon />}
              label={c.estado === 'INACTIVO' ? 'Activar' : 'Desactivar'}
              onClick={() => handleToggleActivo(c)}
            />
            <AdminActionButton
              icon={<TrashIcon />}
              label={tooltips.eliminar ?? 'Eliminar'}
              variant="danger"
              onClick={() => handleBorrar(c)}
            />
          </>
        )}
        footer={
          !loading ? (
            <span>
              {filtered.length} de {centros.length} {adm.centros ?? 'centros'}
            </span>
          ) : undefined
        }
      />

      {modal !== null && (
        <CentroModal
          centro={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
            showToast(adm.saved ?? 'Guardado');
          }}
        />
      )}
    </div>
  );
}

function CentroModal({
  centro,
  onClose,
  onSaved,
}: {
  centro: Centro | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  const isEdit = centro !== null;
  const initialForm: Record<string, string> = isEdit
    ? {
        codigo: centro.codigo,
        nombre: centro.nombre,
        direccion: centro.direccion ?? '',
        ciudad: centro.ciudad ?? '',
        provincia: centro.provincia ?? '',
        codigoPostal: centro.codigoPostal ?? '',
        paisId: centro.paisId ?? centro.pais?.id ?? '',
        telefono: centro.telefono ?? '',
        personaContacto: centro.personaContacto ?? '',
        latitud: centro.latitud != null ? String(centro.latitud) : '',
        longitud: centro.longitud != null ? String(centro.longitud) : '',
        urlUbicacion: centro.urlUbicacion ?? '',
        estado: centro.estado ?? 'ACTIVO',
        organizacionId: centro.organizacionId ?? '',
      }
    : { estado: 'ACTIVO' };
  const [form, setForm] = useState<Record<string, string>>(initialForm);
  const [orgs, setOrgs] = useState<Array<{ value: string; label: string }>>([]);
  const [paises, setPaises] = useState<Array<{ id: string; nombre: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOrganizaciones()
      .then((r) => setOrgs((r.data ?? []).map((o) => ({ value: o.id, label: o.nombre }))))
      .catch(() => {});
    request<{ data: Array<{ id: string; nombre: string }> }>('/paises?activo=true')
      .then((r) => setPaises(r.data ?? []))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.nombre?.trim()) {
      setError('Nombre obligatorio');
      return;
    }
    try {
      setSaving(true);
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        if (v) body[k] = v;
      }
      if (body.latitud) body.latitud = Number(body.latitud);
      if (body.longitud) body.longitud = Number(body.longitud);
      if (isEdit)
        await request(`/apps/centros/${centro.id}`, { method: 'PUT', body: JSON.stringify(body) });
      else await request('/apps/centros', { method: 'POST', body: JSON.stringify(body) });
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };
  const inp: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontSize: 13,
    width: '100%',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: '#5A6171',
    marginBottom: 4,
    display: 'block',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: '3px solid #1F3864',
          maxWidth: 700,
          width: '95%',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit ? 'Editar centro' : 'Nuevo centro'}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <div>
            <label style={lbl}>Codigo</label>
            <input
              style={inp}
              value={form.codigo ?? ''}
              onChange={(e) => set('codigo', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Nombre *</label>
            <input
              style={inp}
              value={form.nombre ?? ''}
              onChange={(e) => set('nombre', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Organizacion</label>
            <select
              style={{ ...inp, background: '#fff' }}
              value={form.organizacionId ?? ''}
              onChange={(e) => set('organizacionId', e.target.value)}
            >
              <option value="">--</option>
              {orgs.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>{adm.direccion ?? 'Direccion'}</label>
            <input
              style={inp}
              value={form.direccion ?? ''}
              onChange={(e) => set('direccion', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Ciudad</label>
            <input
              style={inp}
              value={form.ciudad ?? ''}
              onChange={(e) => set('ciudad', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{adm.provincia ?? 'Provincia'}</label>
            <input
              style={inp}
              value={form.provincia ?? ''}
              onChange={(e) => set('provincia', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{adm.codigoPostal ?? 'Codigo Postal'}</label>
            <input
              style={inp}
              value={form.codigoPostal ?? ''}
              onChange={(e) => set('codigoPostal', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Pais</label>
            <select
              style={{ ...inp, background: '#fff' }}
              value={form.paisId ?? ''}
              onChange={(e) => set('paisId', e.target.value)}
            >
              <option value="">--</option>
              {paises.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>{adm.telefono ?? 'Telefono'}</label>
            <input
              type="tel"
              style={inp}
              value={form.telefono ?? ''}
              onChange={(e) => set('telefono', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{adm.personaContacto ?? 'Persona de contacto'}</label>
            <input
              style={inp}
              value={form.personaContacto ?? ''}
              onChange={(e) => set('personaContacto', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{adm.latitud ?? 'Latitud'}</label>
            <input
              type="number"
              step="any"
              style={inp}
              value={form.latitud ?? ''}
              onChange={(e) => set('latitud', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{adm.longitud ?? 'Longitud'}</label>
            <input
              type="number"
              step="any"
              style={inp}
              value={form.longitud ?? ''}
              onChange={(e) => set('longitud', e.target.value)}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>{adm.urlUbicacion ?? 'Enlace de ubicacion (Google Maps)'}</label>
            <input
              style={inp}
              value={form.urlUbicacion ?? ''}
              onChange={(e) => set('urlUbicacion', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>{adm.estado ?? 'Estado'}</label>
            <select
              style={{ ...inp, background: '#fff' }}
              value={form.estado ?? 'ACTIVO'}
              onChange={(e) => set('estado', e.target.value)}
            >
              <option value="ACTIVO">{adm.activo ?? 'Activo'}</option>
              <option value="INACTIVO">{adm.inactivo ?? 'Inactivo'}</option>
              <option value="EN_APERTURA">En apertura</option>
              <option value="EN_CIERRE">En cierre</option>
            </select>
          </div>
        </div>
        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginTop: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: '#374151',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px',
              background: '#2F5496',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
